// Headless, app-wide auto-trigger for the Phase 1 offline-capture rescue.
//
// Replaces the removed hidden /recover-held-captures page. When an
// authenticated user has the app open, this fires the rescue in the
// background — it reads the device's held orphaned counts (the local-only
// `orphaned_capture_items` table) and ships them to the server inbox via
// `rescueHeldCaptures` (POST /capture/recovery-dump). It renders nothing;
// field staff never see it and never have to navigate anywhere.
//
// When it runs:
//   - once when the session becomes authenticated — this catches counts
//     quarantined in a PRIOR session (the table persists on-device across
//     restarts), which is the actual stranded-device recovery case; and
//   - again when the device comes back online — this catches counts the
//     connector quarantines after a reconnect within the same session.
//     Debounced (MIN_SHIP_INTERVAL_MS) so a flapping radio can't exhaust
//     the endpoint's per-user rate limit right when a real rescue needs
//     to land.
//
// Residual gap (deliberate, documented): on a fresh load the connector
// quarantines AFTER this fires (quarantine happens during uploadData on
// reconnect), so counts orphaned for the first time in THIS session land
// on the next `online` event or the next app load. Closing that fully is
// a follow-up that triggers the ship from the connector right after a
// quarantine; it is out of scope here and not needed for the prior-
// session recovery this component targets.
//
// Safety: read-only on the device (the held rows are NEVER deleted — the
// on-device copy stays intact), writes only to the isolated server inbox,
// and never touches live capture_items / dashboard / CSV / sync. The
// server upsert is idempotent (keyed user + original_item_id), so re-runs
// are safe. Best-effort: a failure is swallowed (the held rows remain, so
// the next trigger retries) and reported to Sentry — never surfaced as UI
// (recovery must be invisible to field staff). A SUCCESSFUL non-empty ship
// ALSO emits one info-level drain beacon per app-load — a Sentry message tagged
// `area:powersync_held_captures_rescued` (a TAG, so it is searchable / alertable;
// the same code is also in `extra.reason` for context) — so an off-device drain
// is visible in Sentry, not only failures — the gap that hid Graeme's 2026-06-30
// rescue (8 held counts shipped to the inbox with no Sentry trace).
//
// Known limitation (shared devices): held rows carry no capturing-user id,
// and `orphaned_capture_items` is wiped only on EXPLICIT logout (logout-
// cleanup.ts). If a different user authenticates WITHOUT the prior user
// logging out (token expiry → re-auth as someone else on a shared device),
// this would ship the prior user's held counts under the new user. This is
// pre-existing in the rescue mechanism; auto-running widens the exposure.
// The correct fix — clearing the local DB on authed-user change — has data-
// deletion semantics and needs product sign-off, so it is a tracked follow-
// up, intentionally NOT bundled into this change. (Codex P2)

import { useEffect } from 'react'

import * as Sentry from '@sentry/react'

import { apiFetch } from '@/hooks/api'
import { db } from '@/hooks/powersync/client'
import { summarizeDrain } from '@/hooks/powersync/drain-summary'
import {
  postDumpItems,
  readQuarantinedItems,
  type ReadableDb,
  type RecoveryDumpItem,
  type RecoveryDumpResponse,
} from '@/hooks/powersync/rescue-held-captures'

import { useAuth } from './auth-provider'

type DumpPoster = (items: RecoveryDumpItem[]) => Promise<RecoveryDumpResponse>

// Module-level so the in-flight guard and debounce survive React re-mounts
// (including StrictMode's dev double-mount), which a component-local ref
// would not — preventing a duplicate ship per load.
let shipInFlight = false
// Sentinel "never successfully shipped" so the first reconnect always clears
// the debounce; replaced with a real timestamp only after a successful send.
let lastShipAt = Number.NEGATIVE_INFINITY
// A reconnecting/flapping radio can fire `online` repeatedly; ignore
// re-runs inside this window so we never burn the POST /capture/recovery-
// dump token bucket (10 / 60s per user).
const MIN_SHIP_INTERVAL_MS = 30_000
// Drain-beacon edge-trigger: emit at most ONE "counts drained off-device" Sentry
// message per app-load. A device permanently holding un-placeable orphans re-ships
// them on every reconnect/app-load (the on-device rows are never deleted), so an
// un-gated beacon would repeat the jam beacon's 1G blow-up (667 events for a single
// cause). Module-level so it survives React re-mounts, like the guards above.
let rescueBeaconSent = false

/** Test seam — reset the module-level guards between cases. */
export function __resetAutoRescueForTest(): void {
  shipInFlight = false
  lastShipAt = Number.NEGATIVE_INFINITY
  rescueBeaconSent = false
}

function postDump(items: RecoveryDumpItem[]): Promise<RecoveryDumpResponse> {
  return apiFetch<RecoveryDumpResponse>('/capture/recovery-dump', {
    method: 'POST',
    json: { items },
  })
}

// Run one rescue, guarding against an overlapping run. This guards
// concurrency (one ship at a time), NOT lifetime — a later trigger after
// this resolves is allowed, so reconnect re-runs still work.
async function shipHeldCounts(
  database: ReadableDb,
  post: DumpPoster,
): Promise<void> {
  if (shipInFlight) return
  shipInFlight = true
  try {
    // Inlines rescueHeldCaptures (readQuarantinedItems → postDumpItems) so the
    // SAME shipped items are in hand for the drain beacon below — no second
    // device read, and the beacon reports exactly what landed.
    const items = await readQuarantinedItems(database)
    const result = await postDumpItems(items, post, items.length)
    // Stamp the debounce window ONLY after a SUCCESSFUL send. Stamping before
    // the await would let a failed attempt (e.g. the app booted with a cached
    // session while still offline) arm the 30s window and suppress the very
    // first reconnect retry, stranding counts until another flap or a reload.
    // The in-flight guard above already prevents an overlapping run, so a
    // failure simply falls through and the next trigger retries. (Codex P2)
    lastShipAt = Date.now()
    // Drain visibility: when held counts actually shipped, emit ONE info-level
    // beacon so a SUCCESSFUL off-device drain is visible in Sentry — previously
    // only failures were captured, so a clean rescue moved a surveyor's counts
    // with no trace ("did his queue drain?" was answerable only from the DB —
    // the gap that hid Graeme's 2026-06-30 rescue of 8 counts). Edge-triggered
    // (rescueBeaconSent) to once per app-load; the summary carries NO PII.
    if (items.length > 0 && !rescueBeaconSent) {
      rescueBeaconSent = true
      try {
        Sentry.captureMessage(
          '[powersync] held counts shipped to recovery inbox — device queue drained off-device',
          {
            level: 'info',
            // `area` is a Sentry TAG — tags are indexed for issue search + alert
            // filters; `extra` is NOT, so the searchable discriminator must live
            // here or the success path stays unobservable (operators watch drains
            // with `area:powersync_held_captures_rescued`). Mirrors the jam/reauth
            // beacons' `tags: { area: … }`. (Codex P2 on PR #829.)
            tags: { area: 'powersync_held_captures_rescued' },
            extra: {
              reason: 'powersync_held_captures_rescued',
              phase: 'auto-rescue-held-captures',
              found: result.found,
              sent: result.sent,
              ...summarizeDrain(items),
            },
          },
        )
      } catch {
        // Telemetry must never disrupt the rescue or the debounce stamping.
      }
    }
  } catch (err: unknown) {
    // Best-effort: the held rows are NOT deleted by the rescue, so a
    // failure just means the next trigger retries. Report it so a device
    // that is silently failing to recover is still visible to us.
    Sentry.captureException(err, {
      level: 'warning',
      extra: { phase: 'auto-rescue-held-captures' },
    })
  } finally {
    shipInFlight = false
  }
}

export interface AutoRescueHeldCapturesProps {
  /** Test seam — inject a fake readable db. Defaults to the PowerSync singleton. */
  database?: ReadableDb
  /** Test seam — inject a fake poster. Defaults to the real recovery-dump POST. */
  post?: DumpPoster
}

export function AutoRescueHeldCaptures({
  database,
  post = postDump,
}: AutoRescueHeldCapturesProps = {}): null {
  const { status } = useAuth()

  useEffect(() => {
    if (status !== 'authenticated') return

    const readable: ReadableDb = database ?? {
      getAll: (sql, params) => db.getAll<Record<string, unknown>>(sql, params),
    }

    // Fire on entry — catches counts quarantined in a prior session.
    void shipHeldCounts(readable, post)

    // Re-run on reconnect — catches counts quarantined after this-session
    // reconnect. Debounced so a flapping radio can't exhaust the rate limit.
    function onOnline(): void {
      if (Date.now() - lastShipAt < MIN_SHIP_INTERVAL_MS) return
      void shipHeldCounts(readable, post)
    }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [status, database, post])

  return null
}
